import { useCallback, useMemo } from 'react';

import { usePostHog } from 'posthog-js/react';

import { useCsvDownloadContext } from '@/components/Chart/context/CsvTableContext';
import type { ChartParentComponent } from '@/components/Chart/hooks/options/useChartFunctions';
import { usePageInformationContext } from '@/context/PageInformationContext';
import useDownloadFileContent from '@/pages/DataHub/Export/ExportPreview/useDownloadFileContent';
import { queryApiByIdString } from '@/utils/queryApi';

export const useAppCsvExport = (tabName: string, disabled?: boolean) => {
  const { downloadFileId, selectedRange } = useCsvDownloadContext();
  const postHog = usePostHog();
  const { mainPageTitle, subPageTitle } = usePageInformationContext();
  const tableParent: ChartParentComponent = useMemo(
    () => ({
      mainPageTitle,
      subPageTitle,
      tabName
    }),
    [mainPageTitle, subPageTitle, tabName]
  );
  const captureChartDownloadAction = useCallback(() => {
    const properties = {
      type: 'csvDownload',
      location: tableParent,
      range: selectedRange
    };

    postHog?.capture('download_button_click', {
      ...properties
    });
  }, [tableParent, postHog, selectedRange]);

  const { downloadFileContent } = useDownloadFileContent(captureChartDownloadAction);

  const handleExport = useCallback(() => {
    if (downloadFileId) {
      queryApiByIdString(downloadFileId, selectedRange).then(data => {
        downloadFileContent(data, 'csv');
      });
    }
  }, [downloadFileContent, downloadFileId, selectedRange]);

  return { handleExport, exportDisabled: disabled || !downloadFileId };
};
